import { slotActionKind } from "../slotActions";
import { parseSessionTopics } from "../../studentSessionHelpers";

export const ROLE_LABEL = {
  LEAD_INSTRUCTOR: "Lead instructor",
  ASSISTANT_INSTRUCTOR: "Assistant instructor",
  TA: "TA",
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOWS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const toDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const shortDate = (iso) => {
  const d = toDate(iso);
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`;
};

export const shortDow = (iso) => DOWS_SHORT[toDate(iso).getDay()];

/** What the instructor would be teaching: the session name, else its first topic, else "Session N". */
export const sessionLabel = (o) => {
  const name = o.sessionName && String(o.sessionName).trim();
  if (name) return name;
  const topic = parseSessionTopics(o.topics)[0];
  if (topic) return topic;
  return o.sessionNumber ? `Session ${o.sessionNumber}` : null;
};

export const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const byDateThenTime = (a, b) =>
  a.sessionDate.localeCompare(b.sessionDate) || String(a.startTime || "").localeCompare(String(b.startTime || ""));

function groupBy(rows, keyOf) {
  const map = new Map();
  rows.forEach((o) => {
    const k = keyOf(o);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(o);
  });
  return [...map.entries()];
}

/** The slot is mine. `assignedToMe` comes from the server; older rows fall back to "filled". */
export const heldByMe = (o) => (o.assignedToMe != null ? o.assignedToMe : o.status === "FILLED");

/** An opening the instructor has already signed up for (pending or turned down) lives under "My requests". */
export const isRequest = (o) => !heldByMe(o) && (o.mySignupStatus === "PENDING" || o.mySignupStatus === "UNAPPROVED");

/** Why an open slot can't be requested right now, or null. */
export const unavailableReason = (o, conflicts) => {
  if (conflicts && conflicts.has(o.id)) return "Time conflict";
  if (o.signupFull) return "Sign-ups full";
  return null;
};

/** Open slots the instructor can still act on, grouped into one card per class + role + sign-up style. */
export function groupOpenSlots(rows) {
  const open = rows.filter((o) => o.status !== "FILLED" && !isRequest(o) && slotActionKind(o));
  return groupBy(open, (o) => `${o.classId}|${o.teachingRole}|${o.signupMode === "APPROVAL" ? "A" : "F"}`)
    .map(([key, list]) => {
      const openings = [...list].sort(byDateThenTime);
      const first = openings[0];
      return {
        key,
        classId: first.classId,
        courseTitle: first.courseTitle || first.sessionName || "Session",
        courseNumber: first.courseNumber,
        category: first.courseCategory,
        role: first.teachingRole,
        approval: first.signupMode === "APPROVAL",
        openings,
      };
    })
    .sort((a, b) => byDateThenTime(a.openings[0], b.openings[0]));
}

/** Role groups folded into one entry per class, so each class shows once with its roles nested under it. */
export function groupByCourse(roleGroups) {
  return groupBy(roleGroups, (g) => String(g.classId)).map(([classId, roles]) => {
    const first = roles[0];
    const openings = roles.flatMap((r) => r.openings).sort(byDateThenTime);
    return {
      key: `course-${classId}`,
      classId: first.classId,
      courseTitle: first.courseTitle,
      courseNumber: first.courseNumber,
      category: first.category,
      roles,
      openings,
    };
  });
}

/** Pending / not-approved sign-ups, one row per class + role + decision. */
export function groupRequests(rows) {
  return groupBy(rows.filter(isRequest), (o) => `${o.classId}|${o.teachingRole}|${o.mySignupStatus}|${o.status}`)
    .map(([key, list]) => {
      const openings = [...list].sort(byDateThenTime);
      const first = openings[0];
      return {
        key,
        id: first.id,
        courseTitle: first.courseTitle || first.sessionName || "Session",
        courseNumber: first.courseNumber,
        category: first.courseCategory,
        role: first.teachingRole,
        status: first.mySignupStatus,
        lost: first.status === "FILLED",
        openings,
      };
    })
    .sort((a, b) => byDateThenTime(a.openings[0], b.openings[0]));
}

export const categoryColor = (category) => `var(--color-cat-${String(category || "other").toLowerCase()}, var(--color-cat-other))`;
